import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { SEARCH_MODAL_KEY } from '@/components/Modal/modal-keys';
import queryKeys from '@/configs/react-query-keys';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services/index';
import useGlobalSearchStore from '@/stores/GlobalSearchStore';
import useDetailModalStore from '@/stores/pure/DetailModalStore';
import { useToast } from '@/stores/pure/ToastStore';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';
import { buildObjQuery, clipboardCopy, timeOut } from '@/utils/util';

const useGlobalSearchModalAction = () => {
  const queryClient = useQueryClient();
  const wr = useWorkspaceRouter();
  const addToast = useToast();
  const location = useLocation();
  const initModal = useDetailModalStore((state) => state.initModal);

  const setSearchStoreState = useGlobalSearchStore(
    (state) => state.setSearchStoreState,
  );

  const toDetailPage = (alias: string) => {
    setSearchStoreState({
      status: true,
      alias: alias,
      searchResult: null,
    });
  };

  const openModal = () => {
    setSearchStoreState({
      status: true,
      alias: null,
    });
    initModal();
  };

  const closeModal = async () => {
    await new Promise<void>((resolve) => {
      setSearchStoreState({
        status: false,
        alias: null,
      });
      wr.navigate(
        {
          pathname: location.pathname,
        },
        { replace: true },
      );
      void timeOut(200).then(() => {
        resolve();
      });
    });
  };

  const copyLink = (alias: string) => {
    const query = buildObjQuery({
      [SEARCH_MODAL_KEY]: alias,
    });
    const url = `${window.location.origin}${window.location.pathname}${query}`;
    clipboardCopy(url);
    addToast('Link copied', 'success');
  };

  const refreshHistoryList = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.search.SEARCH_HISTORY_LIST });
  };

  const addToFilterSingleValue = async (
    searchResult: GlobalSearchResult,
    prevFormats: StructuredFilterQueryFormatT[],
    params: {
      segment: {
        value: StringNumberValue;
        name: string;
      };
      columnKey: string;
    },
  ) => {
    const { segment, columnKey } = params;
    const filter = new SFilter(searchResult.filter);
    const { start_date, end_date } = filter.getDateFilter();

    const newFormats = [
      ...prevFormats,
      {
        columnKey: columnKey,
        operator: STRUCTURED_FILTER_OPERATOR.AND,
        filterValues: [
          {
            filterKey: segment.value,
            filterName: segment.name,
          },
        ],
      },
    ];

    const newFilter = structuredFilterUtils.buildStructuredFilter(newFormats);

    const res = await service.globalSearch.submitSearch({
      title: `${searchResult.title} copy`,
      filter: {
        ...newFilter,
        [FEEDBACK_COLUMNS.DATE_START]: start_date || undefined,
        [FEEDBACK_COLUMNS.DATE_END]: end_date || undefined,
      },
      query: searchResult.query,
      options: {
        ...searchResult.options,
      },
    });

    toDetailPage(res.alias);
    refreshHistoryList();
  };

  return {
    setSearchStoreState,
    toDetailPage,
    openModal,
    closeModal,
    copyLink,
    refreshHistoryList,
    addToFilterSingleValue,
  };
};

export default useGlobalSearchModalAction;
